import type { Period } from 'types/global.types';
import type { TaxTypeEnum } from './taxes.types';

export type DocumentFieldValue = {
  iban: string;
  communication: string;
  type: TaxTypeEnum;
  amount: number;
  deadline: Date | null;
  documentId: string;
  period: {
    year: number;
    month: number;
    quarter: number;
  };
  file_name: string;
  file_hash: string;
  file_path: string;
  referenceType: string;
};

export type DocumentField = {
  referenceType: string;
  type: DocumentReferenceType;
  value: DocumentFieldValue;
};

type DocumentItem = {
  _id: string;
  userId: string;
  LLMResponseId: string;
  tags: TagItemType[];
  guessedTags: TagItemType[];
  AISummary: string;
  dateNumber: number;
  file: {
    name: string;
    path: string;
    hash: string;
  };
  origin: {
    by: string;
    from: string;
  };
  documentFields: DocumentField[];
  documentType: 'expense' | 'revenue' | 'tax' | 'contract' | 'other';
  taxId?: string;
  created: string;
  modified: string;
  opened: boolean;
  __v: number;
};

type TagItemType = {
  _id: string;
  userId: string;
  name: string;
  __v: number;
  created: string;
  modified: string;
  color: string;
};

// Base field structure
type BaseDocumentReferenceField = {
  _id: string;
  userId: string;
  created: string;
  modified: string;
  status?: 'accepted' | 'rejected' | 'pending';
  referenceId?: string;
};

// Base tax item schema (period + tax item enum)
type TaxItemSchema = {
  period: Period;
  type: TaxTypeEnum;
  documentId?: string | null;
};

// User field types
export type DocumentReferenceFieldVATNumber = BaseDocumentReferenceField & {
  type: 'VATNumber';
  value: string;
  referenceType: 'user';
};

export type DocumentReferenceFieldVATRegime = BaseDocumentReferenceField & {
  type: 'VATRegime';
  value: string;
  referenceType: 'user';
};

export type DocumentReferenceFieldVATFrequency = BaseDocumentReferenceField & {
  type: 'VATFrequency';
  value: string;
  referenceType: 'user';
};

export type DocumentReferenceFieldSteuernummer = BaseDocumentReferenceField & {
  type: 'steuernummer';
  value: string;
  referenceType: 'user';
};

// Tax field types
export type DocumentReferenceFieldTaxOther = BaseDocumentReferenceField & {
  type: 'tax';
  value: TaxItemSchema;
  referenceType: 'tax';
};

export type DocumentReferenceFieldTaxPaymentReminder =
  BaseDocumentReferenceField & {
    type: 'tax_payment_reminder';
    value: TaxItemSchema & {
      iban?: string | null;
      communication?: string | null;
      deadline?: string | null;
      amount?: number | null;
    };
    referenceType: 'tax';
  };

export type DocumentReferenceFieldTaxAssessment = BaseDocumentReferenceField & {
  type: 'tax_assessment';
  value: TaxItemSchema & {
    iban?: string | null;
    communication?: string | null;
    deadline?: string | null;
    amount?: number | null;
  };
  referenceType: 'tax';
};

export type DocumentReferenceFieldTaxVatAccountStatement =
  BaseDocumentReferenceField & {
    type: 'tax_vat_account_statement';
    value: TaxItemSchema & {
      statement_date?: string | null;
    };
    referenceType: 'tax';
  };

export type DocumentReferenceFieldTaxReceipt = BaseDocumentReferenceField & {
  type: 'tax_receipt';
  value: TaxItemSchema;
  referenceType: 'tax';
};

export type DocumentReferenceFieldTaxPrepayment = BaseDocumentReferenceField & {
  type: 'tax_prepayment';
  value: TaxItemSchema & {
    last_period_amount?: number | null;
    last_period_deadline?: string | null;
    paid_amounts?: Array<{
      amount?: number | null;
      date?: string | null;
    }>;
  };
  referenceType: 'tax';
};

// Legacy types for backward compatibility (can be removed later)
export type DocumentReferenceFieldUser =
  | DocumentReferenceFieldVATNumber
  | DocumentReferenceFieldVATRegime
  | DocumentReferenceFieldVATFrequency
  | DocumentReferenceFieldSteuernummer;

export type DocumentReferenceFieldTax =
  | DocumentReferenceFieldTaxPaymentReminder
  | DocumentReferenceFieldTaxAssessment
  | DocumentReferenceFieldTaxVatAccountStatement
  | DocumentReferenceFieldTaxReceipt
  | DocumentReferenceFieldTaxPrepayment
  | DocumentReferenceFieldTaxOther;

// Discriminated union of all field types
export type DocumentReferenceField =
  | DocumentReferenceFieldUser
  | DocumentReferenceFieldTax;

export enum DocumentReferenceType {
  tax_vat_account_statement = 'tax_vat_account_statement',
  tax_payment_reminder = 'tax_payment_reminder',
  tax_prepayment = 'tax_prepayment',
  tax_assessment = 'tax_assessment',
  tax_receipt = 'tax_receipt',
  tax = 'tax',
}
export { DocumentItem, TagItemType };
